import type { HashParams } from '@akrababul/shared';
import type { WorkerRequest, WorkerResponse } from './argon-worker.js';
export type { WorkerRequest, WorkerResponse };

export interface WorkerLike {
  postMessage(m: WorkerRequest): void;
  onmessage: ((e: MessageEvent<WorkerResponse>) => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
  terminate(): void;
}
export interface HashAllOptions {
  params: HashParams; salt: string; workerCount: number;
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
  createWorker?: () => WorkerLike;
}
export class HashAbortedError extends Error { constructor() { super('Şifreleme iptal edildi'); this.name = 'HashAbortedError'; } }

function defaultCreateWorker(): WorkerLike {
  return new Worker(new URL('./argon-worker.ts', import.meta.url), { type: 'module' }) as unknown as WorkerLike;
}

export function pickWorkerCount(nav: { hardwareConcurrency?: number | undefined; deviceMemory?: number | undefined }): number {
  if (nav.deviceMemory !== undefined && nav.deviceMemory < 4) return 2;
  return Math.min(4, Math.max(2, nav.hardwareConcurrency ?? 2));
}

/** keyTexts'i worker havuzunda hash'ler; sonuç dizisi girdi sırasındadır. */
export function hashAll(keyTexts: string[], opts: HashAllOptions): Promise<string[]> {
  if (keyTexts.length === 0) return Promise.resolve([]);
  const create = opts.createWorker ?? defaultCreateWorker;
  const n = Math.min(opts.workerCount, keyTexts.length);
  const results: string[] = new Array(keyTexts.length);
  let next = 0, done = 0, settled = false;
  const workers: WorkerLike[] = [];

  return new Promise<string[]>((resolve, reject) => {
    const finish = (fn: () => void) => { if (settled) return; settled = true; workers.forEach((w) => w.terminate()); opts.signal?.removeEventListener('abort', onAbort); fn(); };
    const onAbort = () => finish(() => reject(new HashAbortedError()));
    if (opts.signal?.aborted) return onAbort();
    opts.signal?.addEventListener('abort', onAbort);

    const feed = (w: WorkerLike) => {
      if (next >= keyTexts.length) return;
      const id = next++;
      w.postMessage({ id, keyText: keyTexts[id]!, salt: opts.salt, params: opts.params });
    };
    for (let i = 0; i < n; i++) {
      const w = create();
      workers.push(w);
      w.onmessage = (e) => {
        if (settled) return;
        const d = e.data;
        if ('error' in d) return finish(() => reject(new Error(d.error)));
        results[d.id] = d.hash;
        done++;
        opts.onProgress?.(done, keyTexts.length);
        if (done === keyTexts.length) return finish(() => resolve(results));
        feed(w);
      };
      w.onerror = (e) => finish(() => reject(new Error(e.message || 'Worker hatası')));
      feed(w);
    }
  });
}
